import axios, { type AxiosResponse } from "axios";

import { parseContentDispositionFilename } from "@virtual-brain-lib/utils-text-processing";

import env from "../environments";
import { queryOptions } from "../query-client/queryOption";

type DownloadType = "artifact" | "block-message" | "session" | "content";
type DownloadFormat = "docx" | "xlsx";
type DownloadContentData = { content: string; filename: string };

export const downloadBlob = (response: AxiosResponse<Blob>, defaultDocumentName: string) => {
	const blob = response.data;
	const url = window.URL.createObjectURL(blob);

	const contentDisposition = response.headers["content-disposition"] as string | undefined;
	const filename = parseContentDispositionFilename(contentDisposition) ?? defaultDocumentName;

	const a = document.createElement("a");

	a.href = url;
	a.download = filename;
	a.click();

	window.URL.revokeObjectURL(url);
};

type DownloadDocumentParams = { type: DownloadType; format: DownloadFormat; id: string };

async function requestDocument({ id, type, format }: DownloadDocumentParams) {
	return axios.post<Blob>(`${env.contentApiUrl}/downloads/${type}/${id}/${format}`, null, {
		responseType: "blob",
	});
}

export async function downloadDocument(params: DownloadDocumentParams) {
	const response = await requestDocument(params);

	downloadBlob(response, `document.${params.format}`);
}

/** Returns raw session markdown content, used for copy-to-clipboard. */
export async function getSessionContent({ sessionId }: { sessionId: string }) {
	const response = await axios.get<DownloadContentData>(
		`${env.contentApiUrl}/downloads/session/${sessionId}/content`,
	);

	return response.data;
}

export const getApplicationSessionContent = queryOptions({
	async queryFn({ queryKey }) {
		const [, { sessionId }] = queryKey;
		const response = await axios.get<DownloadContentData>(
			`${env.contentApiUrl}/downloads/session/${sessionId}/content`,
		);

		return response.data;
	},
	queryKey: "applicationSessionContent",
});

export const getApplicationBlockMessageContent = queryOptions({
	async queryFn({ queryKey }) {
		const [, { blockMessageId }] = queryKey;
		const response = await axios.get<DownloadContentData>(
			`${env.contentApiUrl}/downloads/block-message/${blockMessageId}/content`,
		);

		return response.data;
	},
	queryKey: "applicationBlockMessageContent",
});
